import {
  type AvatarSize,
  getAvatarFontSize,
  getOptimizedInitials,
  getOptimizedPictureUrl,
  sizes,
} from "Infrastructure/Utils/avatarUtils";
import { Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";

type UserAvatarProps = {
  picture?: string | null;
  firstName?: string;
  lastName?: string;
  size?: AvatarSize;
  showBorder?: boolean;
};

export const UserAvatar = ({
  picture,
  firstName,
  lastName,
  size = "sm",
  showBorder = false,
}: UserAvatarProps) => {
  const { colors, customFontWeight, radius } = useTheme();
  const [hasError, setHasError] = useState(false);

  const dimension = sizes[size];
  const initials = getOptimizedInitials(firstName, lastName);
  const optimizedPicture = getOptimizedPictureUrl(picture);
  const shouldShowImage = optimizedPicture && !hasError;
  const fontSize = getAvatarFontSize(size);

  return (
    <Stack
      sx={{
        width: dimension,
        height: dimension,
        borderRadius: radius.full,
        overflow: "hidden",
        backgroundColor: shouldShowImage
          ? "transparent"
          : colors.background.inverse,
        border: showBorder ? `2px solid ${colors.border.primary}` : "none",
        alignItems: "center",
        justifyContent: "center",
        cursor: "inherit",
        flexShrink: 0,
      }}
    >
      {shouldShowImage ? (
        <img
          src={optimizedPicture}
          alt={`${firstName ?? ""} ${lastName ?? ""}`.trim()}
          onError={() => setHasError(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <Typography
          variant={fontSize}
          fontWeight={customFontWeight.bold}
          color={colors.text.inverse}
        >
          {initials}
        </Typography>
      )}
    </Stack>
  );
};
